import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const PAGE_MARGIN = 14;
const PAGE_WIDTH = 210;
const PAGE_HEIGHT = 297;
const PDF_FONT = 'NotoSans';

const formatNumber = (value, maximumFractionDigits = 2) => new Intl.NumberFormat('pl-PL', {
  maximumFractionDigits,
  minimumFractionDigits: 0,
}).format(Number(value) || 0);

const formatArea = (value) => {
  const hectares = Number(value);
  if (!Number.isFinite(hectares)) return 'brak danych';
  return `${formatNumber(hectares, 4)} ha (${formatNumber(hectares * 100)} a)`;
};

const formatPercent = (value) => `${formatNumber(value, 1)}%`;

const formatDate = (value) => {
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) return 'brak danych';
  return new Intl.DateTimeFormat('pl-PL', { dateStyle: 'medium', timeStyle: 'short' }).format(date);
};

const makeFilename = (title) => {
  const slug = String(title || 'szkoda')
    .toLocaleLowerCase('pl-PL')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'szkoda';
  const date = new Date().toISOString().slice(0, 10).replaceAll('-', '');
  return `raport-szkody-${slug}-${date}.pdf`;
};

const ensureSpace = (pdf, y, height) => {
  if (y + height <= PAGE_HEIGHT - PAGE_MARGIN) {
    return y;
  }

  pdf.addPage();
  return PAGE_MARGIN;
};

const addSectionTitle = (pdf, title, y) => {
  const nextY = ensureSpace(pdf, y, 11);
  pdf.setFillColor(122, 23, 27);
  pdf.rect(PAGE_MARGIN, nextY, PAGE_WIDTH - PAGE_MARGIN * 2, 7, 'F');
  pdf.setFont(PDF_FONT, 'bold');
  pdf.setFontSize(9);
  pdf.setTextColor(255, 255, 255);
  pdf.text(title, PAGE_MARGIN + 3, nextY + 4.7);
  pdf.setTextColor(30, 30, 30);
  return nextY + 11;
};

const addPageFooter = (pdf) => {
  const pageCount = pdf.getNumberOfPages();
  for (let pageNumber = 1; pageNumber <= pageCount; pageNumber += 1) {
    pdf.setPage(pageNumber);
    pdf.setDrawColor(210, 210, 210);
    pdf.line(PAGE_MARGIN, PAGE_HEIGHT - 11, PAGE_WIDTH - PAGE_MARGIN, PAGE_HEIGHT - 11);
    pdf.setFont(PDF_FONT, 'normal');
    pdf.setFontSize(7);
    pdf.setTextColor(95, 95, 95);
    pdf.text('Raport pomocniczy. Wynik wynika z geometrii AOI, zaznaczonych spotów i ocen operatora.', PAGE_MARGIN, PAGE_HEIGHT - 6);
    pdf.text(`Strona ${pageNumber} z ${pageCount}`, PAGE_WIDTH - PAGE_MARGIN, PAGE_HEIGHT - 6, { align: 'right' });
  }
};

export const downloadPdfReport = async ({ report, mapImage }) => {
  const { notoSansBold, notoSansRegular } = await import('../assets/fonts/notoSans');
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  pdf.addFileToVFS('NotoSans-Regular.ttf', notoSansRegular);
  pdf.addFileToVFS('NotoSans-Bold.ttf', notoSansBold);
  pdf.addFont('NotoSans-Regular.ttf', PDF_FONT, 'normal');
  pdf.addFont('NotoSans-Bold.ttf', PDF_FONT, 'bold');
  pdf.setCharSpace(0);
  const affectedAreaHa = Number(report.affected_area_ha);
  const affectedPercent = Number(report.affected_percent);
  const spotEntries = Array.isArray(report.crop_geometries) ? report.crop_geometries : [];
  const metadata = report.geotiff_metadata || {};
  let y = PAGE_MARGIN;

  pdf.setFillColor(184, 32, 37);
  pdf.rect(0, 0, PAGE_WIDTH, 39, 'F');
  pdf.setFont(PDF_FONT, 'bold');
  pdf.setFontSize(8);
  pdf.setTextColor(255, 255, 255);
  pdf.text('RAPORT SZKODY ŁOWIECKIEJ', PAGE_MARGIN, 10);
  pdf.setFontSize(17);
  const fieldTitle = pdf.splitTextToSize(report.title || 'Pole bez nazwy', PAGE_WIDTH - PAGE_MARGIN * 2);
  pdf.text(fieldTitle.slice(0, 2), PAGE_MARGIN, 18);
  pdf.setFont(PDF_FONT, 'normal');
  pdf.setFontSize(8);
  pdf.text(`Nr raportu: ${report.id || 'brak'}`, PAGE_MARGIN, 34);
  pdf.text(`Wygenerowano: ${formatDate()}`, PAGE_WIDTH - PAGE_MARGIN, 34, { align: 'right' });
  pdf.setTextColor(30, 30, 30);
  y = 46;

  y = addSectionTitle(pdf, 'Wynik oceny', y);
  pdf.setFillColor(122, 23, 27);
  pdf.roundedRect(PAGE_MARGIN, y, PAGE_WIDTH - PAGE_MARGIN * 2, 20, 2, 2, 'F');
  pdf.setFont(PDF_FONT, 'bold');
  pdf.setFontSize(8);
  pdf.setTextColor(255, 243, 176);
  pdf.text('USZKODZENIE POLA', PAGE_MARGIN + 5, y + 6);
  pdf.setFontSize(21);
  pdf.setTextColor(255, 255, 255);
  pdf.text(formatPercent(affectedPercent), PAGE_MARGIN + 5, y + 16);
  pdf.setFontSize(11);
  pdf.setTextColor(255, 243, 176);
  pdf.text(formatArea(affectedAreaHa), PAGE_WIDTH - PAGE_MARGIN - 5, y + 15, { align: 'right' });
  pdf.setTextColor(30, 30, 30);
  y += 26;

  y = addSectionTitle(pdf, 'Pole', y);
  autoTable(pdf, {
    startY: y,
    theme: 'grid',
    styles: { font: PDF_FONT, fontSize: 8, cellPadding: 2.5, textColor: [30, 30, 30] },
    headStyles: { fillColor: [242, 231, 231], textColor: [90, 20, 24], fontStyle: 'bold' },
    head: [['Nazwa pola', 'Uprawa', 'Właściciel', 'Współrzędne']],
    body: [[
      report.title || 'brak danych',
      report.crop_type || 'brak danych',
      report.owner_name || 'brak danych',
      `${formatNumber(report.latitude, 6)}, ${formatNumber(report.longitude, 6)}`,
    ]],
  });
  y = pdf.lastAutoTable.finalY + 6;

  if (mapImage) {
    y = addSectionTitle(pdf, 'Mapa pola i obszarów szkody', y);
    const mapWidth = PAGE_WIDTH - PAGE_MARGIN * 2;
    const imageProperties = pdf.getImageProperties(mapImage);
    const mapHeight = Math.min(96, Math.max(58, mapWidth * (imageProperties.height / imageProperties.width)));
    y = ensureSpace(pdf, y, mapHeight + 7);
    pdf.setFillColor(245, 245, 245);
    pdf.rect(PAGE_MARGIN, y, mapWidth, mapHeight, 'F');
    pdf.addImage(mapImage, 'PNG', PAGE_MARGIN, y, mapWidth, mapHeight, undefined, 'FAST');
    pdf.setDrawColor(180, 180, 180);
    pdf.rect(PAGE_MARGIN, y, mapWidth, mapHeight, 'S');
    y += mapHeight + 6;
  }

  const grossAoiAreaHa = Number(report.aoi_area_ha ?? report.area_ha);
  const exclusionAreaHa = Number(report.exclusion_area_ha) || 0;
  const netAoiAreaHa = Number.isFinite(Number(report.net_aoi_area_ha))
    ? Number(report.net_aoi_area_ha)
    : (Number.isFinite(grossAoiAreaHa) ? Math.max(0, grossAoiAreaHa - exclusionAreaHa) : grossAoiAreaHa);
  const exclusionCount = Number(report.exclusion_count)
    || (Array.isArray(report.exclusion_geometries) ? report.exclusion_geometries.length : 0);
  const hasExclusions = exclusionAreaHa > 0 || exclusionCount > 0;
  const metricsBody = [
    ['Powierzchnia pola 100% (netto)', formatArea(netAoiAreaHa)],
  ];
  if (hasExclusions) {
    metricsBody.push(
      ['Powierzchnia AOI (brutto)', formatArea(grossAoiAreaHa)],
      [`Wyłączenia z pola (${exclusionCount})`, formatArea(exclusionAreaHa)],
    );
  }
  metricsBody.push(
    ['Łączna powierzchnia spotów', formatArea(report.spot_union_area_ha)],
    ['Powierzchnia poza spotami', formatArea(report.remaining_area_ha)],
    ['Szkoda wyliczona w spotach', formatArea(report.spot_damage_area_ha)],
    ['Szkoda uznaniowa poza spotami', formatArea(report.remaining_damage_area_ha)],
    ['Ocena poza spotami', formatPercent(report.remaining_field_damage_percent)],
    ['Oględziny wstępne', report.inspection_damage_percent === null || report.inspection_damage_percent === undefined
      ? 'brak danych'
      : formatPercent(report.inspection_damage_percent)],
  );

  y = addSectionTitle(pdf, 'Metryki obliczenia', y);
  autoTable(pdf, {
    startY: y,
    theme: 'grid',
    styles: { font: PDF_FONT, fontSize: 8, cellPadding: 2.3 },
    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 83 }, 1: { cellWidth: 99 } },
    body: metricsBody,
  });
  y = pdf.lastAutoTable.finalY + 6;

  y = addSectionTitle(pdf, `Zaznaczone powierzchnie (${spotEntries.length})`, y);
  autoTable(pdf, {
    startY: y,
    theme: 'grid',
    styles: { font: PDF_FONT, fontSize: 8, cellPadding: 2.3 },
    headStyles: { fillColor: [242, 231, 231], textColor: [90, 20, 24], fontStyle: 'bold' },
    head: [['Spot', 'Procent zniszczenia', 'Gatunek chroniony']],
    body: spotEntries.length > 0
      ? spotEntries.map((spot, index) => [
        `Spot ${spot.spot_number ?? spot.spotNumber ?? index + 1}`,
        formatPercent(spot.damage_percent ?? spot.damagePercent ?? 100),
        (spot.protected_species ?? spot.protectedSpecies) ? 'tak' : 'nie',
      ])
      : [['Brak zaznaczonych powierzchni', '-', '-']],
  });
  y = pdf.lastAutoTable.finalY + 6;

  y = addSectionTitle(pdf, 'Uwagi', y);
  const notes = report.notes?.trim() || 'Brak uwag.';
  autoTable(pdf, {
    startY: y,
    theme: 'plain',
    styles: { font: PDF_FONT, fontSize: 8.5, cellPadding: 4, fillColor: [252, 247, 247], textColor: [30, 30, 30] },
    body: [[notes]],
  });
  y = pdf.lastAutoTable.finalY + 6;

  y = addSectionTitle(pdf, 'Dane techniczne GeoTIFF', y);
  autoTable(pdf, {
    startY: y,
    theme: 'grid',
    styles: { font: PDF_FONT, fontSize: 7.5, cellPadding: 2.2 },
    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 54 }, 1: { cellWidth: 128 } },
    body: [
      ['Plik', report.geotiff_filename || metadata.source_filename || 'brak danych'],
      ['EPSG', metadata.epsg || metadata.EPSG || metadata.projected_epsg || 'brak danych'],
      ['Czas akwizycji', metadata.datetime || metadata.acquisition_time || 'brak danych'],
      ['Wysokość', metadata.altitude || metadata.flight_height || 'brak danych'],
      ['GSD', metadata.gsd || 'brak danych'],
    ],
  });

  addPageFooter(pdf);
  const fileName = makeFilename(report.title);
  if (window.desktopApi?.savePdfReport) {
    return window.desktopApi.savePdfReport({
      fileName,
      data: new Uint8Array(pdf.output('arraybuffer')),
    });
  }
  await pdf.save(fileName, { returnPromise: true });
};